<template>
  <div>
    <CCommonHeader
      :title="user?.full_name ?? ''"
      :image="user?.avatar_url"
      :sub-title="user?.role"
      no-tabs
    >
      <template #subTitle>
        <div class="flex">
          <Badge
            class="w-auto shrink-0 mt-px capitalize flex items-center"
            :variant="user?.is_email_verified ? 'verified' : 'warning'"
          >
            <span class="icon-verified text-lg !leading-20 mr-1" />{{
              user?.is_email_verified ? $t("verified") : $t("unverified")
            }}
          </Badge>
        </div>
      </template>
      <template #details>
        <CProfileDashDetail
          avatar-class="!rounded-full overflow-hidden"
          v-for="(detail, index) in dataDetails"
          :key="index"
          v-bind="{ ...detail }"
        />
      </template>
      <template #actions>
        <CButton
          v-if="isDesktop"
          class="h-9 flex-center text-xs"
          variant="info"
          icon="icon-edit-2"
          :text="$t('edit')"
          icon-position="left"
          :loading="isLoading"
          @click="handleLinkClick"
        />
      </template>
    </CCommonHeader>
  </div>
</template>

<script setup lang="ts">
import { useMobile } from "@/composables/useMobile";
import CCommonHeader from "@/components/Profile/CCommonHeader.vue";
import CProfileDashDetail from "@/components/Profile/CProfileDashDetail.vue";
import CButton from "@/components/Common/CButton.vue";
import Badge from "../../../components/ui/badge/Badge.vue";
import { computed, ref, watch } from "vue";
import { useAuthStore } from "@/modules/Auth/stores";
import { formatPhoneNumber } from "@/utils";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { uzLatnLocaleData } from "@/modules/Profile/type";
import { useI18n } from "vue-i18n";
import { useLocalizedNavigation } from "@/composables/useLocalizedNavigation";

const { t, locale } = useI18n();
const isLoading = ref(false);
const { navigateTo } = useLocalizedNavigation();

const { isDesktop } = useMobile("desktop");

dayjs.extend(localizedFormat);
dayjs.locale("uz-latn", uzLatnLocaleData);
dayjs.locale("uz-latn");
const availableLocales = ["ru", "en", "uz"];
const fallbackLocale = "ru";

if (!availableLocales.includes(locale.value)) {
  dayjs.locale(fallbackLocale);
} else {
  dayjs.locale(locale.value);
}

const user = computed(() => useAuthStore()?.user);

const dataDetails = computed(() => [
  {
    title: user.value?.display_id,
    description: t("profile.user_id"),
  },
  {
    title: user.value?.referral_number,
    description: t("referral_number"),
  },
  {
    title: formatPhoneNumber(user.value?.phone_number),
    description: t("profile.phone_number"),
  },
  {
    title: user.value?.email,
    description: t("profile.email"),
  },
  {
    title: formatLocalizedDate(user?.value?.created_at),
    description: t("profile.registration_date"),
  },
]);

watch(locale, (newLocale) => {
  if (!availableLocales.includes(newLocale)) {
    dayjs.locale(fallbackLocale);
  } else {
    dayjs.locale(newLocale);
  } // Set Day.js locale to the new language
  updateDataDetails(); // Update the date format in the details
});

// Function to format the date based on the current locale
const formatLocalizedDate = (date: any) => {
  return dayjs(date).locale(locale.value).format("DD MMMM YYYY");
};

// Function to update the date format in dataDetails
const updateDataDetails = () => {
  dataDetails.value[2].title = formatLocalizedDate(user?.value?.created_at);
};

const handleLinkClick = () => {
  isLoading.value = true;
  setTimeout(() => {
    navigateTo({ name: "SettingsProfileEdit" });
  }, 500);
};
</script>

<style scoped></style>
